// src/index.js

// Importar iconos offline ANTES que cualquier otra cosa
import './icons';

import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
// import TagManager from 'react-gtm-module'; // Descomenta y configura si lo usas

import 'bootstrap/dist/css/bootstrap.min.css';
import 'slick-carousel/slick/slick.css';
import './sass/index.scss';

// Importa la configuración de i18n
import i18n from './i18n';

// Tu provider de contexto de traducciones (si lo usas)
import { TranslationProvider } from './contexts/TranslationContext';

const AppWrapper = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Expresión regular para idiomas soportados
  const languageRegex = /^\/(es|en|en-au|pt|fr|it|de)(\/|$)/;
  const currentPath = window.location.pathname;

  // Redirigir de '/au' a '/en-au'
  useEffect(() => {
    const auRegex = /^\/au(\/|$)/;
    if (auRegex.test(currentPath)) {
      const newPath = currentPath.replace(/^\/au/, '/en-au');
      navigate(newPath, { replace: true });
    }
  }, [currentPath, navigate]);

  // Verificar si la URL contiene el idioma; usar 'es' por defecto si no
  const isLanguageInURL = languageRegex.test(currentPath);
  const match = currentPath.match(languageRegex);
  const currentLanguage = isLanguageInURL ? match[1] : 'es';

  // Configurar el atributo lang del <html>
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  /**
   * Inicializar GTM con react-gtm-module (opcional)
   * Solo se hace cuando i18n está inicializado.
   */
  useEffect(() => {
    if (!i18n.isInitialized) return;
    // Ejemplo de inicialización:
    // TagManager.initialize({ gtmId: 'TU_GTM_ID' });
  }, [t, i18n]);

  // Ya no esperamos `ready`, montamos la App de inmediato
  // (las traducciones se precargan y no usan Suspense)

  return <App />;
};

// Función para montar la aplicación
const renderApp = () => {
  const container = document.getElementById('root');
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <BrowserRouter>
          <TranslationProvider>
            <AppWrapper />
          </TranslationProvider>
        </BrowserRouter>
      </HelmetProvider>
    </React.StrictMode>
  );
};

// Espera a que i18n esté inicializado antes de renderizar la aplicación
if (i18n.isInitialized) {
  renderApp();
} else {
  i18n.on('initialized', renderApp);
}
